import { ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ControllerModal,
  DialogContent,
  TextField,
  BpError,
  DialogActions,
  Button,
  Stack,
  valString,
} from '@bymason-private/legacy-pattern-components';
import { useAuth } from '@bymason-private/auth-provider';
import { AccountModel, useInviteUser } from '@bymason/controller-api-hooks';

const emailSchema = valString().email('Must be a valid email address').required('Email is required');

interface InviteUserModalProps {
  open: boolean;
  account: AccountModel;
  onClose: () => void;
  onSuccess?: (email: string) => void;
  onError?: (message: string, code?: number) => void;
}

export function InviteUserModal({ onSuccess, onError, onClose, open, account }: InviteUserModalProps) {
  const { authToken = '' } = useAuth();
  const [inviteUser, , inviteError, inviteBusy] = useInviteUser(authToken);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [pendingInvite, setPendingInvite] = useState('');
  const enableCreate = useMemo(() => email && !emailError, [email, emailError]);

  const handleInviteUser = useCallback(() => {
    if (account) {
      const newEmail = email.trim();
      setPendingInvite(newEmail);
      inviteUser({
        email: newEmail,
        companyId: account.name,
        scopes: ['account:owner'],
      });
    }
  }, [account, email, inviteUser]);

  const handleEmailChange: ChangeEventHandler<HTMLInputElement> = useCallback(async (evt) => {
    setEmail(evt.target.value);
    try {
      await emailSchema.validate(evt.target.value);
      setEmailError('');
    } catch (error) {
      setEmailError(String(typeof error === 'object' ? (error as Error).message : error));
    }
  }, []);

  useEffect(() => {
    if (pendingInvite && !inviteBusy && !inviteError) {
      setPendingInvite('');
      onSuccess && onSuccess(email);
      onClose();
    }
  }, [email, inviteBusy, inviteError, onClose, onSuccess, pendingInvite]);

  useEffect(() => {
    if (inviteError && !inviteBusy) {
      onError && onError(inviteError);
      setPendingInvite('');
    }
  }, [inviteBusy, inviteError, onError]);

  return (
    <ControllerModal onClose={onClose} open={open} title='Invite User'>
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          <TextField
            label='Email'
            error={!!emailError}
            value={email}
            autoFocus
            onChange={handleEmailChange}
            required
            fullWidth
            InputProps={{
              endAdornment: emailError && <BpError color='error' />,
            }}
            helperText={emailError}
            onKeyDown={({ key }) => {
              if (key === 'Enter' && enableCreate) {
                handleInviteUser();
              }
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={onClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button
          color='primary'
          onClick={handleInviteUser}
          size='medium'
          variant='contained'
          disabled={!enableCreate}
        >
          Send invite
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}
